import { useCallback, useState } from 'react';

import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';
import { trackEvent } from '@/utils/fullstory';
import {
	buildZipBlob,
	downloadFilename,
	downloadImageAsPng,
	fetchImageBlob,
	saveBlob,
	toPngBlob,
} from '@/utils/imageDownload';

// Chrome silently drops programmatic downloads fired back-to-back, so the
// sequential fallback spaces them out.
const SEQUENTIAL_SAVE_GAP_MS = 150;

interface UseDownloadPostImagesResult {
	downloadCurrent: () => Promise<void>;
	downloadAll: () => Promise<void>;
	isDownloading: boolean;
	error: string | null;
}

export const useDownloadPostImages = (
	imageUrls: string[],
	artifactId: string,
	activeSlideIndex: number,
): UseDownloadPostImagesResult => {
	const userId = useUserId();
	const [isDownloading, setIsDownloading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const downloadCurrent = useCallback(async () => {
		const url = imageUrls[activeSlideIndex];
		if (!url) {
			setError('No image to download.');
			return;
		}
		setIsDownloading(true);
		setError(null);
		try {
			await downloadImageAsPng(
				url,
				userId,
				downloadFilename(activeSlideIndex + 1, 'png'),
			);
			trackEvent('Post Downloaded', {
				artifact_id: artifactId,
				scope: 'single',
				slide_count: imageUrls.length,
			});
		} catch (err) {
			setError('Failed to download image. Please try again.');
			logger.error('Failed to download post image', err);
		} finally {
			setIsDownloading(false);
		}
	}, [imageUrls, activeSlideIndex, userId, artifactId]);

	const downloadAll = useCallback(async () => {
		if (imageUrls.length === 0) {
			setError('No image to download.');
			return;
		}
		setIsDownloading(true);
		setError(null);
		try {
			const pngs = await Promise.all(
				imageUrls.map(async (url) =>
					toPngBlob(await fetchImageBlob(url, userId)),
				),
			);
			const slideNames = pngs.map((_, i) => downloadFilename(i + 1, 'png'));
			try {
				const entries = await Promise.all(
					pngs.map(async (png, i) => ({
						name: slideNames[i],
						data: new Uint8Array(await png.arrayBuffer()),
					})),
				);
				saveBlob(buildZipBlob(entries), downloadFilename(1, 'zip'));
			} catch (zipErr) {
				// The images are already in hand, so a zip failure shouldn't lose
				// the download — save each slide individually instead.
				logger.error(
					'Failed to build carousel zip; falling back to per-slide downloads',
					zipErr,
				);
				for (let i = 0; i < pngs.length; i += 1) {
					if (i > 0) {
						await new Promise((resolve) =>
							setTimeout(resolve, SEQUENTIAL_SAVE_GAP_MS),
						);
					}
					saveBlob(pngs[i], slideNames[i]);
				}
			}
			trackEvent('Post Downloaded', {
				artifact_id: artifactId,
				scope: 'all',
				slide_count: imageUrls.length,
			});
		} catch (err) {
			setError('Failed to download images. Please try again.');
			logger.error('Failed to download post images', err);
		} finally {
			setIsDownloading(false);
		}
	}, [imageUrls, userId, artifactId]);

	return { downloadCurrent, downloadAll, isDownloading, error };
};
